import { useState, useEffect } from 'react';
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";

import {
  LayoutDashboard,
  Users,
  GraduationCap,
  School,
  Menu,
  X,
  BarChart2,
  ChevronLeft,
  ChevronRight,
  Building,
  BookOpen,
  Store
} from "lucide-react";
import { Link, useLocation } from 'react-router-dom';
import { useIsMobile } from '@/hooks/use-mobile';

interface DashboardLayoutProps {
  children: React.ReactNode;
}

const DashboardLayout = ({ children }: DashboardLayoutProps) => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();
  const isMobile = useIsMobile();

  // Close mobile menu on route change
  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location.pathname]);

  const menuItems = [
    { icon: LayoutDashboard, label: 'Dashboard', path: '/admin' },
    { icon: Users, label: 'Users', path: '/admin/users' },
    { icon: GraduationCap, label: 'Trainings', path: '/admin/trainings' },
    { icon: Building, label: 'Providers', path: '/admin/providers' },
    { icon: BookOpen, label: 'My Trainings', path: '/my-trainings' },
    { icon: Store, label: 'My Providers', path: '/admin/my-providers' },
  ];
  const toggleSidebar = () => {
    if (isMobile) {
      setIsMobileMenuOpen(!isMobileMenuOpen);
    } else {
      setIsSidebarOpen(!isSidebarOpen);
    }
  };

  return (
    <div className="min-h-screen bg-background/50 pt-16">
      {/* Mobile Toggle Button */}
      {isMobile && (
        <button
          onClick={toggleSidebar}
          className="fixed top-20 left-4 z-50 p-2 rounded-lg bg-white shadow-md hover:bg-muted/80 transition-colors"
        >
          {isMobileMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      )}

      {/* Sidebar */}
      <aside
        className={cn(
          "fixed left-0 top-16 z-40 h-[calc(100vh-4rem)] transition-all duration-300 ease-in-out bg-white border-r shadow-sm",
          {
            "w-64": isSidebarOpen,
            "w-20": !isSidebarOpen && !isMobile,
            "w-64 -translate-x-full": !isMobileMenuOpen && isMobile,
            "w-64 translate-x-0": isMobileMenuOpen && isMobile,
          }
        )}
      >
        <div className="h-16 flex items-center justify-between px-4 border-b bg-muted/10">
          <div className="flex items-center gap-3">
            {!isMobile && (
              <Button
                variant="ghost"
                size="icon"
                onClick={toggleSidebar}
                className="hover:bg-muted transition-colors"
              >
                {isSidebarOpen ? <ChevronLeft className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
              </Button>
            )}
            <h1 className={cn("font-bold transition-all duration-300 text-primary", {
              "opacity-100 translate-x-0": isSidebarOpen || isMobile,
              "opacity-0 -translate-x-4": !isSidebarOpen && !isMobile,
            })}>
              Admin Dashboard
            </h1>
          </div>
        </div>

        <ScrollArea className="h-[calc(100vh-8rem)] py-4">
          <nav className="space-y-1.5 px-2">
            {menuItems.map((item) => (
              !isSidebarOpen && !isMobile ? (
                <Tooltip key={item.path} delayDuration={0}>
                  <TooltipTrigger asChild>
                    <Link
                      to={item.path}
                      className={cn(
                        "flex items-center justify-center rounded-lg p-3 transition-all duration-200 group",
                        location.pathname === item.path
                          ? "bg-primary text-primary-foreground shadow-md"
                          : "hover:bg-muted text-muted-foreground hover:text-foreground"
                      )}
                    >
                      <item.icon className="h-5 w-5 transition-transform group-hover:scale-110" />
                    </Link>
                  </TooltipTrigger>
                  <TooltipContent side="right" className="font-medium">
                    {item.label}
                  </TooltipContent>
                </Tooltip>
              ) : (
                <Link
                  key={item.path}
                  to={item.path}
                  className={cn(
                    "flex items-center gap-3 rounded-lg px-3 py-2.5 transition-all duration-200 group",
                    location.pathname === item.path
                      ? "bg-primary text-primary-foreground shadow-md"
                      : "hover:bg-muted text-muted-foreground hover:text-foreground"
                  )}
                >
                  <item.icon className="h-5 w-5 flex-shrink-0 transition-transform group-hover:scale-110" />
                  <span className={cn("font-medium transition-opacity duration-200", {
                    "opacity-100": isSidebarOpen,
                    "opacity-0": !isSidebarOpen && !isMobile,
                  })}>{item.label}</span>
                </Link>
              )
            ))}
          </nav>
        </ScrollArea>
      </aside>

      {/* Mobile Overlay */}
      {isMobile && isMobileMenuOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-30 backdrop-blur-sm transition-opacity duration-300"
          onClick={() => setIsMobileMenuOpen(false)}
        />
      )}

      {/* Main Content */}
      <main
        className={cn(
          "min-h-[calc(100vh-4rem)] transition-all duration-300 ease-in-out px-4",
          isMobile ? "ml-0" : isSidebarOpen ? "ml-64" : "ml-20"
        )}
      >
        <div className="max-w-7xl mx-auto py-8">
          {children}
        </div>
      </main>
    </div>
  );
};

export default DashboardLayout;